import { useOptionalParticipationFlow } from "~/components/participation/ParticipationFlow";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  StripeCardCvcElementOptions,
  StripeCardExpiryElementOptions,
  StripeCardNumberElementOptions,
  StripeError,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
} from "@stripe/stripe-js";
import { FormEvent, useEffect, useRef, useState, type ReactNode } from "react";
import { FaCcAmex, FaCcDiscover, FaCcMastercard, FaCcVisa } from "react-icons/fa6";
import { FiChevronDown } from "react-icons/fi";
import clsx from "clsx";
import { PrimaryButton } from "~/components/Button";
import { trackLegacyCheckout as track } from "~/utils/analytics";
import { useRootLocale } from "~/root";
import { getCountryCode } from "~/utils/localization";
import { useCheckoutSession } from "./CheckoutSession";
import { CheckoutFooter } from "./CheckoutFooter";

export type PreparedPayment = { clientSecret: string; walletIntentID: string };

type PropTypes = {
  eventId: string;
  eventTitle: string;
  preparePayment: () => Promise<PreparedPayment | null>;
  totalCents: number;

  children: ReactNode;
  onBack?: () => void;
  backDisabled?: boolean;
  error?: string | null;
};

type PaymentMethod = "card" | "apple-pay" | "google-pay";

const COUNTRIES = [
  { code: "AU", name: "Australia" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "NZ", name: "New Zealand" },
  { code: "CA", name: "Canada" },
];

const ELEMENT_BASE_STYLE: StripeCardNumberElementOptions["style"] = {
  base: {
    color: "#4A5043",
    fontFamily: "Proxima-Nova, system-ui, sans-serif",
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "24px",
    "::placeholder": { color: "#C7C7CD" },
  },
  invalid: {
    color: "#df1b41",
    iconColor: "#df1b41",
  },
};

const cardNumberOptions: StripeCardNumberElementOptions = {
  showIcon: false,
  placeholder: "1234 1234 1234 1234",
  style: ELEMENT_BASE_STYLE,
};

const expiryOptions: StripeCardExpiryElementOptions = {
  placeholder: "MM / YY",
  style: ELEMENT_BASE_STYLE,
};

const cvcOptions: StripeCardCvcElementOptions = {
  placeholder: "CVC",
  style: ELEMENT_BASE_STYLE,
};

const expressOptions: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    applePay: "auto",
    googlePay: "auto",
    link: "never",
    paypal: "never",
    amazonPay: "never",
    klarna: "never",
  },
  buttonHeight: 48,
  layout: { maxColumns: 1, overflow: "never" },
};

export const PurchaseForm = ({
  eventId,
  eventTitle,
  preparePayment,

  children,
  onBack,
  backDisabled = false,
  error,
}: PropTypes) => {
  const flow = useOptionalParticipationFlow();
  const scrollRef = useRef<HTMLDivElement>(null);
  const paymentInFlight = useRef(false);
  const locale = useRootLocale();

  const checkout = useCheckoutSession();
  const countryCode = getCountryCode(locale);
  const stripe = useStripe();
  const elements = useElements();

  const [walletsAvailable, setWalletsAvailable] = useState(false);
  const [country, setCountry] = useState<string>(countryCode);
  const [message, setMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);

  const buildReturnUrl = (walletIntentID: string) => {
    const returnUrl = new URL(
      `${window.location.origin}/plans/${eventId}/purchase-processing`,
    );
    if (walletIntentID) {
      returnUrl.searchParams.set("walletIntentID", walletIntentID);
    }
    return returnUrl.toString();
  };

  const checkPaymentForRetry = async (payment: PreparedPayment) => {
    let status;
    try {
      const result = await stripe?.retrievePaymentIntent(payment.clientSecret);
      status = result?.paymentIntent?.status;
    } catch {
      // Keep the same attempt locked until its payment state can be verified.
    }
    if (!checkout.canContinue()) return false;
    if (status === "requires_payment_method" || status === "requires_confirmation" || status === "requires_action") {
      checkout.update({ paymentPhase: "method" });
      return true;
    }
    if (status) {
      window.location.assign(buildReturnUrl(payment.walletIntentID));
    } else {
      setMessage("We couldn’t check your payment. Check your connection and try again.");
    }
    return false;
  };

  const handleStripeError = async (error: StripeError, paymentMethod: PaymentMethod, payment?: PreparedPayment) => {
    if (error.type === "card_error" || error.type === "validation_error") {
      if (payment) checkout.update({ paymentPhase: "method" });
      setMessage(error.message ?? "Check your payment details and try again.");
    } else if (payment) {
      if (await checkPaymentForRetry(payment)) {
        setMessage("Your payment hasn’t been completed. Please try again.");
      }
    } else {
      setMessage("We couldn’t prepare your payment. Please try again.");
    }
    track("checkout_payment_failed", {
      event_id: eventId,
      event_title: eventTitle,
      error_type: error.type,
      error_code: error.code,
      payment_method: paymentMethod,
    });
  };

  const submitCard = async (e: FormEvent) => {
    e.preventDefault();

    if (paymentInFlight.current || !stripe || !elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setMessage("Payment form not ready. Please try again.");
      return;
    }

    paymentInFlight.current = true;
    setMessage(undefined);
    setIsLoading(true);

    if (!await checkout.flush() || !checkout.canContinue()) {
      paymentInFlight.current = false;
      setIsLoading(false);
      return;
    }
    const payment = await preparePayment();
    if (!payment || !checkout.canContinue()) {
      paymentInFlight.current = false;
      setIsLoading(false);
      return;
    }
    if (checkout.draft.paymentPhase === "processing" && !await checkPaymentForRetry(payment)) return false;
    // A submitted confirmation can settle after this page unloads. Retain the
    // attempt until Stripe confirms whether it needs payment entry or processing.
    checkout.update({ paymentPhase: "processing" });
    const { error } = await stripe.confirmCardPayment(payment.clientSecret, {
      payment_method: {
        card: cardNumberElement,
        billing_details: {
          address: { country },
        },
      },
    }).catch(() => ({ error: { type: "api_connection_error" as const } }));

    if (error) {
      await handleStripeError(error, "card", payment);
      return false;
    }

    if (checkout.canContinue()) window.location.assign(buildReturnUrl(payment.walletIntentID));
    return true;
  };

  const submitExpress = async ({ expressPaymentType }: StripeExpressCheckoutElementConfirmEvent) => {
    if (paymentInFlight.current || !stripe || !elements) return;

    paymentInFlight.current = true;
    setMessage(undefined);
    setIsLoading(true);

    if (!await checkout.flush() || !checkout.canContinue()) {
      paymentInFlight.current = false;
      setIsLoading(false);
      return;
    }
    const submitted = await elements.submit();
    if (submitted.error) {
      await handleStripeError(submitted.error, expressPaymentType === "apple_pay" ? "apple-pay" : "google-pay");
      paymentInFlight.current = false;
      setIsLoading(false);
      return;
    }
    const payment = await preparePayment();
    if (!payment || !checkout.canContinue()) {
      paymentInFlight.current = false;
      setIsLoading(false);
      return;
    }
    if (checkout.draft.paymentPhase === "processing" && !await checkPaymentForRetry(payment)) return false;
    checkout.update({ paymentPhase: "processing" });
    const { error } = await stripe.confirmPayment({
      clientSecret: payment.clientSecret,
      elements,
      confirmParams: { return_url: buildReturnUrl(payment.walletIntentID) },
    }).catch(() => ({ error: { type: "api_connection_error" as const } }));

    if (error) {
      await handleStripeError(error, expressPaymentType === "apple_pay" ? "apple-pay" : "google-pay", payment);
      paymentInFlight.current = false;
      setIsLoading(false);
      return false;
    }
    return true;
  };

  const submit = async (work: () => Promise<unknown>) => {
    if (paymentInFlight.current || flow?.pending) return;
    try {
      if (flow) await flow.run("confirm_payment", work);
      else await work();
    } catch {
      // A transport failure after confirmation has an unknown financial outcome.
      // Preserve processing recovery and never create a second attempt here.
      setMessage("We couldn’t confirm your payment. Check your connection and try again.");
    } finally {
      paymentInFlight.current = false;
      setIsLoading(false);
    }
  };
  const onCardSubmit = (event: FormEvent) => { event.preventDefault(); return submit(() => submitCard(event)); };
  const onExpressConfirm = (event: StripeExpressCheckoutElementConfirmEvent) => submit(() => submitExpress(event));

  useEffect(() => {
    if ((message || error) && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [message, error]);

  return (
    <form
      onSubmit={onCardSubmit}
      className="flex min-h-0 flex-1 flex-col sm:block"
    >
      <div ref={scrollRef} className="-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-[30px] pb-4 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0">
        {children}
        <div className="mt-5 flex flex-col gap-3">
          <div hidden={!walletsAvailable} inert={isLoading}>
            <div className="flex flex-col gap-3">
              <p className="font-sans text-sm font-bold text-butter-green leading-[1.4]">
                Payment method
              </p>
              <ExpressCheckoutElement
                onConfirm={onExpressConfirm}
                onReady={({ availablePaymentMethods }) => setWalletsAvailable(
                  !!(availablePaymentMethods?.applePay || availablePaymentMethods?.googlePay),
                )}
                onLoadError={() => setWalletsAvailable(false)}
                options={expressOptions}
              />
              <div aria-hidden="true" className="border-t border-butter-green/10" />
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-xl bg-white p-4 sm:p-5">
            <FieldShell label="Card number">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <CardNumberElement options={cardNumberOptions} />
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <FaCcVisa
                    size={22}
                    aria-label="Visa"
                    style={{ color: "#1A1F71" }}
                  />
                  <FaCcMastercard
                    size={22}
                    aria-label="Mastercard"
                    style={{ color: "#EB001B" }}
                  />
                  <FaCcAmex
                    size={22}
                    aria-label="American Express"
                    style={{ color: "#006FCF" }}
                  />
                  <FaCcDiscover
                    size={22}
                    aria-label="Discover"
                    style={{ color: "#FF6000" }}
                  />
                </div>
              </div>
            </FieldShell>

            <div className="flex gap-3">
              <FieldShell className="flex-1" label="Expiry date">
                <CardExpiryElement options={expiryOptions} />
              </FieldShell>
              <FieldShell className="flex-1" label="Security code">
                <CardCvcElement options={cvcOptions} />
              </FieldShell>
            </div>

            <FieldShell label="Country">
              <div className="relative">
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="block w-full appearance-none bg-transparent pr-6 font-sans text-base text-butter-green outline-none"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <FiChevronDown
                  size={14}
                  className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-butter-green"
                  aria-hidden
                />
              </div>
            </FieldShell>
          </div>
          {(message || error) && <p role="alert" className="font-sans text-sm text-butter-pink">{message || error}</p>}
        </div>
      </div>
      <CheckoutFooter onBack={onBack ? () => { if (!paymentInFlight.current) onBack(); } : undefined} backDisabled={isLoading || backDisabled}>
        <PrimaryButton type="submit" className="text-base!" loading={isLoading}>
          Finalise
        </PrimaryButton>
      </CheckoutFooter>
    </form>
  );
};

type FieldShellProps = {
  label: string;
  className?: string;
  children: React.ReactNode;
};

function FieldShell({ label, className, children }: FieldShellProps) {
  return (
    <div className={clsx("flex flex-col gap-1.5", className)}>
      <span className="font-sans text-sm font-bold text-butter-green leading-[1.4]">
        {label}
      </span>
      <div className="rounded-[9px] border border-butter-cream bg-white px-3 py-3 transition-colors focus-within:border-butter-green/60">
        {children}
      </div>
    </div>
  );
}
